import { Injectable, Inject } from '@angular/core';
import { EventBusService } from '@icc/common/event-bus.service';
import { WindowActiveConfiguration } from '@icc/common/configurations/WindowActiveConfiguration';
import { LoadedConfiguratorsDataValue } from '@icc/common/configurators/configurators-data.service';
import { core } from '@icc/common/helpers';
import { Common } from '@icc/common/Common';
import { IssuesService, IssueLevel, ModalService } from '@icc/helpers';
import { ValidationService } from '@icc/common/configurators/validation.service';
import { APP_CONFIG, AppConfig, AppConfigFactory } from '@icc/common/config';
import { SiliconeColorsDefaultsService } from '@icc/common/colors/silicone-colors-defaults.service';
import { SiliconeColorsPageComponent } from 'libs/configurator/window/src/lib/silicone-colors-page/silicone-colors-page.component';
import { TranslateService } from '@icc/common';

@Injectable()
export class SiliconeColorsService {
    colors = [];

    private loadedData = false;

    constructor(
        private eventBusService: EventBusService,
        private modalService: ModalService,
        @Inject(APP_CONFIG) private config: AppConfigFactory,
        private issuesService: IssuesService,
        private validationService: ValidationService,
        private siliconeColorsDefaultsService: SiliconeColorsDefaultsService,
        private translateService: TranslateService
    ) {
        if (this.config().IccConfig.Configurators.siliconeColor) {
            eventBusService.subscribe<LoadedConfiguratorsDataValue>('setSystem', data => {
                if (
                    (data.activeConfiguration as WindowActiveConfiguration).System.type
                    !== 'wood'
                ) {
                    this.issuesService.unregister('no-silicone-colors', data.activeConfiguration);
                }
            });
            eventBusService.subscribe<LoadedConfiguratorsDataValue>(
                'setConstructionColor',
                data => {
                    if (
                        WindowActiveConfiguration.is(data.activeConfiguration)
                        && WindowActiveConfiguration.is(data.defaultConfiguration)
                    ) {
                        this.setDefaultColor(data.activeConfiguration, data.defaultConfiguration);
                    }
                }
            );

            eventBusService.subscribe<LoadedConfiguratorsDataValue>(
                'loadedConfiguratorsData',
                data => {
                    this.loadColors(data.value);
                    this.loadedData = true;
                    if (data.activeConfiguration) {
                        this.setDefaultColor(
                            data.activeConfiguration as WindowActiveConfiguration,
                            data.defaultConfiguration as WindowActiveConfiguration
                        );
                    }
                }
            );
        }
    }

    loadColors(data) {
        this.colors = data.windowSiliconeColors || [];
    }

    setDefaultColor(conf: WindowActiveConfiguration, defaultConf) {
        if (!this.loadedData || conf.System.type !== 'wood') {
            return;
        }
        this.issuesService.unregister('no-silicone-colors', conf);
        if (this.colors.length === 0) {
            this.issuesService.simpleRegister(
                'no-silicone-colors',
                'Brak kolorów silikonów!',
                this.translateService.instant('WINDOW|Brak kolorów silikonów!'),
                conf,
                {
                    level: IssueLevel.ERROR,
                    logLevel: IssueLevel.WARN,
                    showMessage: true,
                }
            );
            this.validationService.invalid(conf, 'siliconeColor');
            return;
        }
        const shouldBeChanged =
            !Common.isObject(conf.SiliconeColor)
            || !conf.SiliconeColor.id
            || conf.SiliconeColor.isDefault;
        if (shouldBeChanged) {
            const defaultColor = this.siliconeColorsDefaultsService.getDefaultColor(
                this.colors,
                conf,
                defaultConf
            );
            this.setColor(defaultColor, true, conf, defaultConf);
        } else {
            Object.assign(conf, this.validationService.valid(conf, 'siliconeColor'));
        }
    }

    setColor(color, isDefault = false, conf: WindowActiveConfiguration, defaultConf) {
        color = core.copy(color);
        color.isDefault = isDefault;
        conf.SiliconeColor = core.copy(color);
        defaultConf.SiliconeColor = color;
        Object.assign(conf, this.validationService.valid(conf, 'siliconeColor'));
        this.eventBusService.post({
            key: 'setSiliconeColor',
            value: {
                colorId: color.id,
            },
        });
    }

    openModal(conf: WindowActiveConfiguration, defaultConf) {
        this.modalService
            .open({
                templateUrl: 'modalSiliconeColor.html',
                controller: 'ModalSiliconeColorCtrl as msiliconecolor',
                pageComponent: SiliconeColorsPageComponent,
                resolve: {
                    colors: () => this.colors,
                    selColor: () => conf.SiliconeColor,
                },
            })
            .result.then(selectedColor => {
                if (selectedColor) {
                    this.setColor(selectedColor, false, conf, defaultConf);
                }
            });
    }
}
